import {
	useCallback, useEffect, useRef, useState,
} from 'react';
import Socket from '../../connection/socket';
import {
	ClientOpentokHealth,
	HealthStatsMessage,
} from '../../stores/types/opentok-health';

/** Drop reports older than this so a client that left stops being shown. */
export const REPORT_STALE_AFTER_MS = 30000;

interface UseClientHealthReportsResult {
	/** Latest report per client, keyed by gr_user. */
	reports: ClientOpentokHealth[];
	/** True once the channel is joined and a request has gone out. */
	connected: boolean;
	requestNow: () => void;
}

/**
 * Joins a meeting's socket channel read-only and collects the OpenTok health
 * reports its clients send back.
 *
 * This is the hybrid half of the dashboard: WebRTC quality only exists inside
 * each participant's browser, so it cannot be read from any server. The
 * dashboard never calls `userConnected`, so it never appears in the room's
 * connected-user roster and never triggers session bookkeeping - it only listens.
 */
export default function useClientHealthReports(
	roomId: number | null,
	/** 0 pauses requesting; reports pushed on degradation still arrive. */
	intervalMs: number,
): UseClientHealthReportsResult {
	const [reports, setReports] = useState<Map<string, ClientOpentokHealth>>(new Map());
	const [connected, setConnected] = useState(false);
	const socketRef = useRef<Socket | null>(null);

	const requestNow = useCallback(() => {
		socketRef.current?.sendMessage(HealthStatsMessage.Request, { requestedAt: Date.now() });
	}, []);

	useEffect(() => {
		if (!roomId) return undefined;

		setConnected(false);
		setReports(new Map());

		const socket = new Socket(`meeting-${roomId}`, undefined, () => {
			setConnected(true);
			socket.sendMessage(HealthStatsMessage.Request, { requestedAt: Date.now() });
		});
		socketRef.current = socket;

		socket.addListener(HealthStatsMessage.Report, (message: any) => {
			const report = message?.message as ClientOpentokHealth | undefined;
			if (!report) return;

			setReports((prev) => {
				const next = new Map(prev);
				// Key on gr_user so a client that reconnects replaces its old row
				// instead of appearing twice.
				next.set(String(report.grUser ?? report.webrtcID ?? report.name), report);
				return next;
			});
		});

		return () => {
			socketRef.current = null;
			socket.disconnect();
		};
	}, [roomId]);

	useEffect(() => {
		if (!connected || !intervalMs) return undefined;

		const timer = setInterval(requestNow, intervalMs);
		return () => clearInterval(timer);
	}, [connected, intervalMs, requestNow]);

	// Expire reports rather than showing a frozen snapshot of someone who left.
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		const timer = setInterval(() => setNow(Date.now()), 5000);
		return () => clearInterval(timer);
	}, []);

	const fresh = Array.from(reports.values())
		.filter((report) => now - report.collectedAt < REPORT_STALE_AFTER_MS)
		.sort((a, b) => Number(b.isProducer) - Number(a.isProducer) || a.name.localeCompare(b.name));

	return { reports: fresh, connected, requestNow };
}
